"use client";
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  Card,
  CardBody,
  CardFooter,
  Typography,
  Input,
} from "@material-tailwind/react";
import { useRouter } from "next/navigation";
import userApi from "@/api/user";
import profileService from "@/features/profile/service";
import authService from "@/features/auth/services";
import InputPassword from "./InputPassword";
import { tracker } from "@/helpers/openReplay";

interface Props {
  isOpen: boolean;
  onClose: (value: boolean) => void;
}

type TModel = {
  email: string;
  password: string;
};

export function DialogSignin({ isOpen, onClose }: Props) {
  const router = useRouter();
  const [open, setOpen] = useState(isOpen);
  const [isLoading, setIsLoading] = useState(false);
  const [model, setModel] = useState<TModel>({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string>();

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const handleOpen = () => {
    onClose(!open);
    setOpen((cur) => !cur);
  };

  const onSetModel = (key: keyof TModel, value: string) => {
    setModel((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      setError(undefined);
      setIsLoading(true);
      e.preventDefault();
      const { data, status } = await userApi.signin(model);
      if (status !== 200) {
        setError(data.message);
        return;
      }
      authService.setLoggedIn();
      const user = await profileService.getUser();
      if (user?.email) {
        tracker.setUserID(user.email);
      }
      router.replace(`/?u=${user?.id}`);
      handleOpen();
    } catch (error) {
      setError("มีบางอย่างผิดพลาด กรุณาลองอีกครั้ง");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Dialog
        size="xs"
        open={open}
        handler={handleOpen}
        className="bg-transparent shadow-none font-noto"
      >
        <Card className="mx-auto w-full max-w-[24rem]">
          <form action="" onSubmit={onSubmit}>
            <CardBody className="flex flex-col gap-4">
              <Typography variant="h4" color="blue-gray" className="font-noto">
                เข้าระบบสมาชิก
              </Typography>
              <div>
                <Typography
                  className="font-normal font-noto"
                  variant="paragraph"
                  color="gray"
                >
                  กรอกอีเมลและรหัสผ่านเพื่อเข้าใช้งานระบบ
                </Typography>
                <Typography
                  className="mb-3 font-normal font-noto"
                  variant="small"
                  color="gray"
                >
                  (หากเข้าใช้งานระบบไม่ได้ ติดต่อ Line{" "}
                  <a
                    href="https://line.me/ti/p/~@agentsrikrung"
                    target="_blank"
                    className="text-blue-400"
                  >
                    @agentsrikrung
                  </a>
                  )
                </Typography>
              </div>
              <Typography className="-mb-2 font-noto" variant="h6">
                อีเมล
              </Typography>
              <Input
                label="กรอกอีเมล"
                size="lg"
                required
                className="font-noto"
                value={model.email}
                onChange={(e) => onSetModel("email", e.target.value)}
                disabled={isLoading}
              />
              <Typography className="-mb-2 font-noto" variant="h6">
                รหัสผ่าน
              </Typography>
              <div>
                <InputPassword
                  value={model.password}
                  onChange={(v) => onSetModel("password", v)}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => {
                    router.push("/req-reset-password");
                    handleOpen();
                  }}
                  className="text-[12px] text-blue-500"
                  name="forgot-password"
                  aria-label="ลืมรหัสผ่าน - ขอรีเซ็ตรหัสผ่านใหม่"
                >
                  ลืมรหัสผ่าน
                </button>
              </div>
              {error && (
                <Typography
                  variant="small"
                  className="mt-4 flex justify-center"
                  color="red"
                >
                  {error}
                </Typography>
              )}
            </CardBody>

            <CardFooter className="pt-0">
              <Button
                fullWidth
                className="bg-[#188F46] font-noto"
                type="submit"
                disabled={isLoading}
                name="signin-submit"
                aria-label="เข้าระบบสมาชิก - ยืนยันการเข้าสู่ระบบ"
              >
                เข้าระบบสมาชิก
              </Button>
              <Typography
                variant="small"
                className="mt-4 flex justify-center font-noto"
              >
                คุณยังไม่ได้เป็นสมาชิกใช่หรือไม่?
                <Typography
                  as="a"
                  href="#signup"
                  variant="small"
                  color="blue-gray"
                  className="ml-1 font-bold font-noto"
                  onClick={() => {
                    handleOpen();
                    router.push("/sign-up");
                  }}
                >
                  สมัครสมาชิกเว็บไซต์
                </Typography>
              </Typography>
            </CardFooter>
          </form>
        </Card>
      </Dialog>
    </div>
  );
}
